import { ReactNode } from 'react';

import { ChevronDown } from '@freelancelabs/icons';
import { clsx } from '@freelancelabs/utils';

/**
 * One labelled cell of a header row that names itself by several facts rather than
 * by a title.
 */
export interface CollapsibleSectionField {
    id: string;
    label: ReactNode;
    value: ReactNode;
    /**
     * The cell holds a sentence **about** the field rather than the field's value:
     * « non disponible », « pas encore affiché ici ».
     *
     * It is set apart on purpose: rendered in the weight and colour of a value, such
     * a sentence is read as one, and in a cell that usually holds an amount that is
     * the difference between « we do not serve this » and a figure.
     */
    muted?: boolean;
}

interface CollapsibleSectionBaseProps {
    /**
     * Whether the section is unfolded. **Controlled, and deliberately so**: the
     * screens that group a list this way offer « tout ouvrir » and « tout fermer »,
     * which no component-held state can serve. A section that remembered its own
     * state would disagree with those two buttons the first time they were used.
     */
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /**
     * The chip at the end of the header row.
     *
     * **The caller writes the words, and the component never counts.** What the
     * figure counts carries a business rule this side cannot know: a section header
     * has to say « 12 prestations », and whether that 12 is the complete count of the
     * group or the length of a loaded page is exactly the distinction the portal's
     * counter rule turns on.
     */
    count?: ReactNode;
    /**
     * **Rendered into the DOM as soon as the section exists, open or closed.**
     * That is how `<details>` works, and it is the trap of this component. A caller
     * whose panel opens a query must gate that query on `open` itself; mounting a
     * panel per section would fire one query per section on first paint, which is the
     * very thing folding the list was meant to avoid.
     */
    children: ReactNode;
    /**
     * A pictogram set at the start of the header row, inside its own tinted square.
     *
     * **It marks the start of a line, it does not distinguish the sections.** The V3
     * mockup says so in its own comment after trying the other way: « le même
     * pictogramme vingt fois n'informe de rien ». Give it the icon of the screen the
     * sections belong to, not one per section.
     */
    icon?: ReactNode;
    className?: string;
}

/**
 * Two ways for a header to name what it folds, and a section uses one or the other.
 *
 * A **title** names a group by what its rows have in common, the mockup's
 * `.marche-groupe`. **Fields** name a single object by several of its facts at once,
 * each under its own label, the mockup's `.commande-resume`, whose header reads
 * « Commande / Période / Montant / Statut ». Offering both at the same time would put
 * a heading above a row of labelled cells, which is a heading for the first cell and
 * nothing else.
 */
export type CollapsibleSectionProps = CollapsibleSectionBaseProps &
    (
        | {
              /** What the section groups, in a few words. */
              title: ReactNode;
              /**
               * The facts that qualify the title on the same line: which account it is
               * read under, a reference, a short qualifier. Secondary to the title and
               * never a substitute for it.
               */
              meta?: ReactNode;
              fields?: undefined;
          }
        | {
              fields: CollapsibleSectionField[];
              title?: undefined;
              meta?: undefined;
          }
    );

/**
 * A foldable section of a long list: a header, an optional count, a chevron.
 *
 * Shape taken from `.marche-groupe` of the V2 mockup, which draws these groups as a
 * native `<details>`. The element is kept rather than rebuilt from a button and a
 * div: it carries the keyboard handling, the semantics and the browser's own
 * find-in-page expansion for free.
 *
 * The mockup draws a second header on the same element, `.commande-resume`, whose
 * row is a grid of labelled cells rather than a title: that is the `fields` variant,
 * and both are the same fold. Building the second as its own component would have
 * given the portal two accordions with two ideas of what a chevron, a focus ring and
 * a fold are.
 *
 * **Nothing interactive goes inside the header.** `<summary>` is an activation
 * surface, so a link nested in it both navigates and folds the section, and a link
 * inside a button is not a shape assistive technology can announce. A gesture the
 * section offers belongs in the panel, under the header that names it.
 */
const CollapsibleSection = ({
    open,
    onOpenChange,
    title,
    meta,
    fields,
    count,
    children,
    icon,
    className,
}: CollapsibleSectionProps) => (
    <details
        className={clsx('fl-collapsible-section', className)}
        open={open}
        // The browser has already toggled by the time this fires: the event says what
        // the element became, and the caller's state follows it rather than guessing.
        onToggle={(event) => onOpenChange(event.currentTarget.open)}
    >
        <summary className="fl-collapsible-section__summary">
            {icon && (
                // `aria-hidden` : le pictogramme est le meme sur toutes les sections,
                // donc annonce il ferait entendre vingt fois la meme chose avant le nom
                // qui, lui, distingue.
                <span className="fl-collapsible-section__chip" aria-hidden="true">
                    {icon}
                </span>
            )}
            {fields ? (
                <span className="fl-collapsible-section__fields">
                    {fields.map((field) => (
                        <span key={field.id} className="fl-collapsible-section__field">
                            <span className="fl-collapsible-section__field-label">{field.label}</span>
                            <span
                                className={clsx(
                                    'fl-collapsible-section__field-value',
                                    field.muted && 'fl-collapsible-section__field-value--muted'
                                )}
                            >
                                {field.value}
                            </span>
                        </span>
                    ))}
                </span>
            ) : (
                <span className="fl-collapsible-section__heading">
                    <span className="fl-collapsible-section__title">{title}</span>
                    {meta && <span className="fl-collapsible-section__meta">{meta}</span>}
                </span>
            )}
            {count !== undefined && <span className="fl-collapsible-section__count">{count}</span>}
            {/* Rotated by CSS rather than by the icon's `rotate` prop, which writes an
                inline transform and would leave nothing for the transition to animate. */}
            <ChevronDown className="fl-collapsible-section__chevron" size={16} aria-hidden="true" />
        </summary>
        <div className="fl-collapsible-section__panel">{children}</div>
    </details>
);

export default CollapsibleSection;
